import { useConvexAuth, useQuery } from "convex/react";
import { api } from '../../convex/_generated/api'
import { MODULE, PLAN_FEATURES, type ModuleKey, type PlanFeatureKey } from '@/lib/planFeatures'

/**
 * Frontend feature gate. Values must match the strings stored in `plan.features`
 * (the source of truth lives in `src/lib/planFeatures.ts :: PLAN_FEATURES`).
 * Re-exported here so call sites can keep using `FEATURE.X`.
 */
export const FEATURE = PLAN_FEATURES
export type FEATURE = PlanFeatureKey

export { MODULE }
export type { ModuleKey }

const useOrganizationPermissions = () => {
  // Gate on Convex's auth state (not Clerk's isSignedIn): during sign-out
  // isSignedIn can lag behind the dropped Convex token, firing this query
  // unauthenticated. See ConditionalLayout for the full rationale.
  const { isAuthenticated } = useConvexAuth()
  const result = useQuery(api.features.getFeatures, isAuthenticated ? {} : 'skip');
  const features = result?.features;
  const modules = result?.modules as ModuleKey[] | undefined;

  const has = (options: { feature: FEATURE }) => {
    return features?.includes(options.feature) ?? false;
  };

  const hasModule = (moduleKey: ModuleKey) => {
    return modules?.includes(moduleKey) ?? false;
  };

  return {
    has,
    hasModule,
    features,
    modules,
    isLoading: result === undefined,
  };
};

interface ProtectProps {
  feature: FEATURE;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export const Protect: React.FC<ProtectProps> = ({ feature, children, fallback }) => {
  const { has, isLoading } = useOrganizationPermissions();

  if (isLoading) {
    return null;
  }

  if (!has({ feature })) {
    return fallback ?? null;
  }

  return <>{children}</>;
}

export default useOrganizationPermissions;